import { useState } from "react";
import { Globe, Check, ChevronDown, History } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Flag } from "@/components/Flag";

export const LanguageSwitcher = ({ compact = false }) => {
  const { lang, setLang, languages, recentLangs } = useI18n();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const current = languages.find((l) => l.code === lang);
  const list = languages.filter(
    (l) =>
      l.name.toLowerCase().includes(q.toLowerCase()) ||
      l.native.toLowerCase().includes(q.toLowerCase())
  );
  const recent = (recentLangs || [])
    .filter((c) => c !== lang)
    .map((c) => languages.find((l) => l.code === c))
    .filter(Boolean);

  const Row = ({ l, testPrefix = "language-option" }) => (
    <button
      data-testid={`${testPrefix}-${l.code}`}
      onClick={() => {
        setLang(l.code);
        setOpen(false);
      }}
      className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-start text-sm transition-colors hover:bg-surfacealt"
    >
      <Flag code={l.code} testId={`${testPrefix === "language-option" ? "language-flag" : "recent-flag"}-${l.code}`} />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium" dir="auto">{l.native}</span>
        <span className="block truncate text-xs text-ink-soft">{l.name}</span>
      </span>
      {l.code === lang && <Check className="h-4 w-4 shrink-0 text-brand" />}
    </button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="language-switcher-trigger"
          className="inline-flex min-h-[40px] items-center gap-2 rounded-full border border-white/15 bg-panel px-4 py-2 text-sm font-medium transition-colors hover:border-brand/50 hover:text-brand active:scale-95"
        >
          <Globe className="h-4 w-4 shrink-0" />
          {!compact && (
            <>
              <Flag code={current?.code || "en"} testId="current-language-flag" />
              <span className="truncate max-w-[8rem]">{current?.native || "English"}</span>
            </>
          )}
          <ChevronDown className="h-3 w-3 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-2" data-testid="language-switcher-panel">
        <Input
          data-testid="language-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search 50 languages…"
          className="mb-2 h-9 rounded-xl"
        />
        <div className="max-h-64 overflow-y-auto cml-scroll">
          {!q && recent.length > 0 && (
            <div data-testid="recent-languages" className="mb-1 border-b border-white/10 pb-1">
              <p className="flex items-center gap-1.5 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-ink-soft">
                <History className="h-3 w-3" /> Recent
              </p>
              {recent.map((l) => (
                <Row key={`recent-${l.code}`} l={l} testPrefix="recent-language" />
              ))}
            </div>
          )}
          {list.map((l) => (
            <Row key={l.code} l={l} />
          ))}
          {list.length === 0 && <p className="p-3 text-sm text-ink-soft">No match</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
};
